import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from './Modal.jsx';
import Button from './Button.jsx';

/**
 * Rückfragen als Promise: `const confirm = useConfirm();`
 *
 *   if (!(await confirm({ title: 'Löschen?', text: '…', variant: 'danger', confirmLabel: 'Löschen' }))) return;
 *
 * Warum: Bis September 2026 liefen 28 Rückfragen und 18 Meldungen über
 * `window.confirm()`/`alert()` — Browser-Dialoge, die weder Theme noch Schrift
 * noch Tastaturfokus der App kennen und auf dem iPhone als System-Sheet
 * erscheinen. `Modal.Confirm` gab es längst, brauchte aber pro Stelle eigenen
 * Zustand (open/pending). Dieser Provider hält den Zustand EINMAL und macht die
 * Migration zu einer Zeile: `window.confirm(msg)` → `await confirm({ text: msg })`.
 *
 * `okOnly: true` liefert einen reinen Hinweis-Dialog (ein Knopf) — für Inhalte,
 * die der Nutzer lesen und ggf. abschreiben muss (z. B. ein temporäres Passwort),
 * wo ein Toast zu flüchtig wäre.
 *
 * Ohne Provider (Unit-Tests einzelner Komponenten) fällt der Hook auf
 * `window.confirm` zurück, damit bestehende Spies weiter greifen.
 */

const ConfirmContext = createContext(null);

export const ConfirmProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback((options) => {
    const opts = typeof options === 'string' ? { text: options } : (options || {});
    return new Promise((resolve) => {
      // Eine offene Rückfrage wird durch eine neue verdrängt (als abgelehnt).
      if (resolverRef.current) resolverRef.current(false);
      resolverRef.current = resolve;
      setDialog(opts);
    });
  }, []);

  const settle = useCallback((result) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setDialog(null);
    if (resolve) resolve(result);
  }, []);

  const value = useMemo(() => confirm, [confirm]);

  const {
    title,
    text,
    confirmLabel = 'Bestätigen',
    cancelLabel = 'Abbrechen',
    variant = 'primary',
    okOnly = false,
  } = dialog || {};

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {okOnly ? (
        <Modal
          open={dialog !== null}
          onClose={() => settle(true)}
          title={title}
          size="sm"
          footer={<Button onClick={() => settle(true)}>{confirmLabel === 'Bestätigen' ? 'Verstanden' : confirmLabel}</Button>}
        >
          <div className="whitespace-pre-line text-sm text-foreground">{text}</div>
        </Modal>
      ) : (
        <Modal.Confirm
          open={dialog !== null}
          onClose={() => settle(false)}
          onConfirm={() => settle(true)}
          title={title}
          confirmLabel={confirmLabel}
          cancelLabel={cancelLabel}
          variant={variant}
        >
          <div className="whitespace-pre-line">{text}</div>
        </Modal.Confirm>
      )}
    </ConfirmContext.Provider>
  );
};

ConfirmProvider.propTypes = {
  children: PropTypes.node,
};

// Fallback ohne Provider: Browser-Dialog, damit isolierte Tests (Spies auf
// window.confirm) und Storybook-artige Einzelrenderings weiter funktionieren.
const fallbackConfirm = (options) => {
  const opts = typeof options === 'string' ? { text: options } : (options || {});
  if (opts.okOnly) {
    if (typeof window !== 'undefined' && typeof window.alert === 'function') window.alert(opts.text);
    return Promise.resolve(true);
  }
  return Promise.resolve(typeof window !== 'undefined' && typeof window.confirm === 'function'
    ? window.confirm(opts.text)
    : true);
};

export const useConfirm = () => {
  const confirm = useContext(ConfirmContext);
  return confirm || fallbackConfirm;
};

export default ConfirmProvider;
